import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const defaultMapper = <T, TActual = T>(actualState: TActual) => actualState as T extends TActual ? T : never;

type HookReturnValue<T> = [
  state: T,
  setState: React.Dispatch<React.SetStateAction<T>>,
  resetState: () => void,
];

/**
 * Allows the use of a state that is dependant on another and is automatically updated when the actual state changes.
 * @param actualState The actual state that is used.
 * @param mapper An optional function that maps the actual state to the resulting state.
 */
export default function useDependantState<TActual, T = TActual>(actualState: TActual, mapper: (actualState: TActual) => T = defaultMapper): HookReturnValue<T> {
  const [internalState, setInternalState] = useState<T>(mapper(actualState));

  const mapperRef = useRef(mapper);
  mapperRef.current = mapper;

  useEffect(
    () => {
      setInternalState(mapperRef.current(actualState));
    },
    [actualState],
  );

  const resetState = useCallback(
    () => setInternalState(mapper(actualState)),
    [actualState, mapper],
  );

  return useMemo<HookReturnValue<T>>(
    () => [internalState, setInternalState, resetState],
    [internalState, resetState],
  );
}
